/**
 * Quote Aggregation Manager
 * Creates supplier quotes from customer position configurations with direct file upload
 * Based on ModernSupplierCaseCreationForm.tsx but with aggregation logic
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  RequestQuote as QuoteIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as UploadIcon,
  AttachFile as AttachFileIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { caseManagementService, CreateSupplierCaseRequest } from '../../../services/caseManagementService';
import { unifiedOrderService } from '../../../services/unifiedOrderService';
import { supplierService, Supplier } from '../../../services/supplierService';
import { 
  MaterialData, 
  HeatTreatmentData, 
  CoatingData, 
  ToolData, 
  CarbideData
} from '../../../types/unifiedOrderTypes';
import { MaterialFields, HeatTreatmentFields, CoatingFields, ToolFields, CarbideFields } from '../../forms/TypeSpecificFields';
import SupplierSelector from '../supplier/SupplierSelector';
import { CustomerQuotePosition } from '../../../types/unifiedOrderTypes';
import { PositionTypeSelection, PositionType } from './PositionTypeSelector';

interface QuoteAggregationManagerProps {
  open: boolean;
  onClose: () => void;
  customerCaseId: string;
  positions: CustomerQuotePosition[];
  positionTypeSelections: Map<string, PositionTypeSelection>;
  onQuotesCreated: (quotes: any[]) => void;
}

interface AggregatedQuoteByType {
  type: PositionType;
  positions: {
    customerPosition: CustomerQuotePosition;
    configuration: any;
    quantity: number;
    customerPositionId: string;
    customerPositionReference: number;
  }[];
  suppliers: Supplier[];
  header: {
    title: string;
    description?: string;
    priority: number;
    special_instructions?: string;
  };
}

interface SupplierQuoteData {
  type: PositionType;
  supplier: Supplier;
  positions: {
    position_number: number;
    quantity: number;
    part_name: string;
    part_number?: string;
    notes?: string;
    type_specific_data: any;
    // Customer relationship tracking
    customer_case_id: string;
    customer_position_id: string;
    customer_position_reference: number;
  }[];
  header: {
    title: string;
    description?: string;
    priority: number;
    special_instructions?: string;
  };
}

interface PositionFileMap {
  [key: string]: { // positionId_type format
    files: File[];
    positionIndex: number;
    type: PositionType;
  };
}

export default function QuoteAggregationManager({
  open,
  onClose,
  customerCaseId,
  positions,
  positionTypeSelections,
  onQuotesCreated
}: QuoteAggregationManagerProps) {
  const [aggregatedQuotes, setAggregatedQuotes] = useState<AggregatedQuoteByType[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [positionFiles, setPositionFiles] = useState<PositionFileMap>({});
  const [selectedSuppliers, setSelectedSuppliers] = useState<Map<PositionType, string[]>>(new Map());
  const [quoteHeaders, setQuoteHeaders] = useState<Map<PositionType, any>>(new Map());
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const { showNotification } = useNotification();

  const steps = ['Review Aggregation', 'Select Suppliers', 'Configure Headers', 'Upload Files', 'Create Quotes'];

  // Initialize aggregation on mount
  useEffect(() => {
    if (open) {
      console.log('🎯 QuoteAggregationManager initialized:', {
        customerCaseId,
        positionsCount: positions.length,
        selectionsCount: positionTypeSelections.size,
        open
      });
      
      generateAggregation();
      loadSuppliers();
    } else {
      console.log('🎯 QuoteAggregationManager closed');
    }
  }, [open, positions, positionTypeSelections]);

  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getSuppliers();
      if (response.success) {
        setSuppliers(response.data || []);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const generateAggregation = () => {
    const typeGroups: Map<PositionType, any[]> = new Map();

    console.log('\n🔍 [AGGREGATION-DEBUG] generateAggregation called');
    console.log('📊 [AGGREGATION-DEBUG] Input data:', {
      customerCaseId,
      positionsCount: positions.length,
      positionTypeSelectionsSize: positionTypeSelections.size,
      positionTypeSelectionsEntries: Array.from(positionTypeSelections.entries())
    });

    // Group positions by selected types
    Array.from(positionTypeSelections.entries()).forEach(([positionId, selection]) => {
      const position = positions.find(p => p.id === positionId);
      console.log(`🔍 [AGGREGATION-DEBUG] Processing position ${positionId}:`, {
        position: !!position,
        selection: selection,
        selectedTypes: selection?.selectedTypes
      });
      
      if (!position) return;

      selection.selectedTypes
        .filter(type => type !== 'no_supplier_quotes') // Skip "no supplier quotes" type
        .forEach(type => {
        console.log(`🎯 [AGGREGATION-DEBUG] Processing type "${type}" for position ${positionId}`);
        
        if (!typeGroups.has(type)) {
          typeGroups.set(type, []);
          console.log(`📋 [AGGREGATION-DEBUG] Created new type group for "${type}"`);
        }

        const configuration = selection.typeConfigurations[type] || {};
        typeGroups.get(type)!.push({
          customerPosition: position,
          configuration,
          quantity: selection.quantity || 1,
          customerPositionId: position.id,
          customerPositionReference: position.position_number
        });
        console.log(`✅ [AGGREGATION-DEBUG] Added position to type group "${type}"`);
      });
    });

    // Convert to aggregated quotes
    const aggregated: AggregatedQuoteByType[] = Array.from(typeGroups.entries()).map(([type, positions]) => ({
      type,
      positions,
      suppliers: [],
      header: {
        title: `${type.toUpperCase()} Quote Request - Customer ${customerCaseId}`,
        description: `Aggregated ${type} positions from customer case ${customerCaseId}`,
        priority: 3,
        special_instructions: ''
      }
    }));

    console.log('🎯 [AGGREGATION-DEBUG] Final aggregation result:', {
      typeGroupsCount: typeGroups.size,
      typeGroupsTypes: Array.from(typeGroups.keys()),
      aggregatedCount: aggregated.length,
      aggregatedTypes: aggregated.map(a => a.type)
    });
    
    console.log('📋 [AGGREGATION-DEBUG] Detailed aggregation:', aggregated.map(a => ({
      type: a.type,
      positionsCount: a.positions.length,
      positionIds: a.positions.map(p => p.customerPositionId)
    })));

    setAggregatedQuotes(aggregated);
    
    // Initialize empty supplier selections and headers
    const supplierMap = new Map<PositionType, string[]>();
    const headerMap = new Map<PositionType, any>();
    
    aggregated.forEach(quote => {
      supplierMap.set(quote.type, []);
      headerMap.set(quote.type, quote.header);
    });
    
    setSelectedSuppliers(supplierMap);
    setQuoteHeaders(headerMap);
  };

  const updateSupplierSelection = (type: PositionType, supplierIds: string[]) => {
    const newMap = new Map(selectedSuppliers);
    newMap.set(type, supplierIds);
    setSelectedSuppliers(newMap);
  };

  const updateQuoteHeader = (type: PositionType, field: string, value: any) => {
    const currentHeader = quoteHeaders.get(type) || {};
    const updatedHeader = { ...currentHeader, [field]: value };
    const newMap = new Map(quoteHeaders);
    newMap.set(type, updatedHeader);
    setQuoteHeaders(newMap);
  };

  const handleFileUpload = (positionId: string, type: PositionType, files: File[]) => {
    const key = `${positionId}_${type}`;
    const newFileMap = { ...positionFiles };
    
    // Find position index for later reference
    const positionIndex = aggregatedQuotes
      .find(q => q.type === type)?.positions
      .findIndex(p => p.customerPositionId === positionId) || 0;
    
    newFileMap[key] = {
      files,
      positionIndex,
      type
    };
    
    setPositionFiles(newFileMap);
    
    console.log(`📁 Files uploaded for ${key}:`, {
      filesCount: files.length,
      fileNames: files.map(f => f.name),
      positionIndex,
      type
    });
  };

  const generateSupplierQuotes = async () => {
    try {
      setLoading(true);
      const createdQuotes: any[] = [];

      for (const aggregation of aggregatedQuotes) {
        const supplierIds = selectedSuppliers.get(aggregation.type) || [];
        if (supplierIds.length === 0) continue;

        // Get all suppliers for this type
        const selectedSuppliersForType = supplierIds
          .map(id => suppliers.find(s => s.id === id))
          .filter((supplier): supplier is Supplier => supplier !== undefined);
        if (selectedSuppliersForType.length === 0) continue;

        const header = quoteHeaders.get(aggregation.type) || {};

        // Create supplier quote positions with customer relationship tracking
        const supplierPositions = aggregation.positions.map((pos, index) => ({
          position_number: index + 1,
          quantity: pos.quantity,
          part_name: pos.customerPosition.part_name,
          part_number: pos.customerPosition.part_number || `${pos.customerPosition.part_name}-${pos.customerPosition.position_number}`,
          notes: pos.customerPosition.notes || pos.customerPosition.part_name,
          type_specific_data: {
            ...pos.configuration,
            // Customer relationship tracking
            customer_case_id: customerCaseId,
            customer_position_id: pos.customerPositionId,
            customer_position_reference: pos.customerPositionReference,
            is_aggregated: true,
            original_customer_position: {
              part_name: pos.customerPosition.part_name,
              part_number: pos.customerPosition.part_number,
              notes: pos.customerPosition.notes
            }
          }
        }));

        // Create ONE supplier case per type with ALL suppliers (like ModernSupplierCaseCreationForm)
        const caseCreateRequest: CreateSupplierCaseRequest = {
          case_type: 'supplier_case',
          case_sub_type: 'quote_request',
          customer_email: selectedSuppliersForType[0].email, // Primary supplier email
          customer_name: selectedSuppliersForType.length > 1 
            ? `Multi-Supplier ${aggregation.type.toUpperCase()} Quote`
            : selectedSuppliersForType[0].name,
          customer_company: selectedSuppliersForType[0].company_name,
          title: header.title,
          metadata: {
            order_type: aggregation.type,
            // Include ALL suppliers in metadata (like ModernSupplierCaseCreationForm)
            suppliers: selectedSuppliersForType.map(supplier => ({
              id: supplier.id,
              email: supplier.email,
              name: supplier.name,
              company: supplier.company_name
            })),
            supplier_id: selectedSuppliersForType[0].id, // Primary supplier
            positions: supplierPositions,
            // Store customer case relationship for quote response linking
            customer_case_relationship: {
              customer_case_id: customerCaseId,
              customer_positions: aggregation.positions.map(p => ({
                customer_position_id: p.customerPositionId,
                customer_position_reference: p.customerPositionReference
              }))
            },
            // Email distribution to all suppliers
            email: {
              to_addresses: selectedSuppliersForType.map(s => s.email),
              cc_addresses: [],
              distribution_note: selectedSuppliersForType.length > 1 
                ? `Quote request sent to ${selectedSuppliersForType.length} suppliers: ${selectedSuppliersForType.map(s => s.name).join(', ')}`
                : undefined
            }
          }
        };

        // Create supplier case with temporary order
        const response = await caseManagementService.createSupplierCase(caseCreateRequest);
        
        if (!response.success) {
          throw new Error(`Failed to create ${aggregation.type} quote for ${selectedSuppliersForType.map(s => s.name).join(', ')}: ${response.message}`);
        }

        const createdCase = response.data as any;
        console.log(`✅ Created supplier quote: ${createdCase.case_number} for ${selectedSuppliersForType.map(s => s.name).join(', ')} (${aggregation.type})`);

        // CRITICAL: Set parent_case_id relationship for customer-supplier case linking
        try {
          console.log(`🔗 Setting parent_case_id relationship for ${createdCase.case_number}`);
          const parentLinkResponse = await fetch(`/api/v3/cases/${createdCase.id}/parent`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              parent_case_id: customerCaseId,
              relationship_type: 'quote_request',
              customer_case_id: customerCaseId
            })
          });

          if (parentLinkResponse.ok) {
            const linkResult = await parentLinkResponse.json();
            if (linkResult.success) {
              console.log(`✅ Successfully linked supplier case ${createdCase.case_number} to customer case ${customerCaseId}`);
            } else {
              console.warn(`⚠️ Failed to link cases: ${linkResult.message}`);
            }
          } else {
            console.warn(`⚠️ Parent case linking failed: ${parentLinkResponse.status}`);
          }
        } catch (linkError) {
          console.warn(`⚠️ Error linking parent case:`, linkError);
          // Continue execution - the case is created, just without the parent relationship
        }

        // Create customer position to supplier case mappings for tracking
        for (const pos of aggregation.positions) {
          try {
            const mappingResponse = await caseManagementService.mapSupplierQuoteToPosition(
              pos.customerPositionId,
              {
                supplier_case_id: createdCase.id,
                supplier_position_number: pos.customerPositionReference,
                quoted_price: 0, // Initial placeholder - will be updated when quote is received
                delivery_time_days: null,
                supplier_notes: `Initial mapping for ${aggregation.type} quote request`
              }
            );
            
            if (mappingResponse.success) {
              console.log(`📎 Mapped customer position ${pos.customerPositionReference} to supplier case ${createdCase.case_number}`);
            }
          } catch (mappingError) {
            console.warn(`⚠️ Failed to create mapping for position ${pos.customerPositionReference}:`, mappingError);
            // Continue with other mappings even if one fails
          }
        }

        // Upload files if any exist for this type
        if (createdCase.temp_order_id) {
          await uploadFilesForQuote(createdCase, aggregation.type);
        }

        createdQuotes.push({
          ...createdCase,
          suppliers: selectedSuppliersForType,
          type: aggregation.type,
          customer_relationship: {
            customer_case_id: customerCaseId,
            customer_positions: aggregation.positions.map(p => ({
              customer_position_id: p.customerPositionId,
              customer_position_reference: p.customerPositionReference
            }))
          }
        });
      }

      console.log(`🎉 Successfully created ${createdQuotes.length} supplier quotes:`);
      createdQuotes.forEach(quote => {
        const supplierNames = quote.suppliers?.map((s: any) => s.name).join(', ') || 'Unknown Supplier';
        console.log(`  ${quote.case_number} (${supplierNames} - ${quote.type})`);
      });

      onQuotesCreated(createdQuotes);
      showNotification(`Successfully created ${createdQuotes.length} supplier quotes!`, 'success');
      onClose();

    } catch (error: any) {
      console.error('Error generating supplier quotes:', error);
      showNotification(error.message || 'Failed to generate supplier quotes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const uploadFilesForQuote = async (createdCase: any, type: PositionType) => {
    const relevantFiles = Object.entries(positionFiles).filter(([key, data]) => 
      data.type === type
    );

    if (relevantFiles.length === 0) return;

    console.log(`📤 Uploading files for ${type} quote ${createdCase.case_number}...`);

    for (const [key, fileData] of relevantFiles) {
      try {
        const createdPositions = createdCase.positions || [];
        const targetPosition = createdPositions[fileData.positionIndex];
        
        if (!targetPosition?.position_id) {
          console.error(`❌ No position found for index ${fileData.positionIndex}`);
          continue;
        }

        const uploadResponse = await unifiedOrderService.uploadPositionFiles(
          createdCase.temp_order_id,
          targetPosition.position_id,
          fileData.files,
          `${type} specification files`
        );

        if (uploadResponse.success) {
          console.log(`✅ Files uploaded for position ${fileData.positionIndex}: ${fileData.files.map(f => f.name).join(', ')}`);
        } else {
          console.error(`❌ File upload failed for position ${fileData.positionIndex}:`, uploadResponse.message);
        }

      } catch (error: any) {
        console.error(`💥 File upload error:`, error);
      }
    }
  };

  const renderStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0: // Review Aggregation
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Aggregated Quotes by Type
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Customer case {customerCaseId} has been aggregated into {aggregatedQuotes.length} quote type(s).
            </Alert>
            
            {aggregatedQuotes.map((quote, index) => (
              <Card key={quote.type} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    {quote.type.toUpperCase()} Quote ({quote.positions.length} positions)
                  </Typography>
                  <List dense>
                    {quote.positions.map((pos, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={`Position ${pos.customerPositionReference}: ${pos.customerPosition.part_name}`}
                          secondary={`Quantity: ${pos.quantity} | Customer Position ID: ${pos.customerPositionId}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 1: // Select Suppliers
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Suppliers for Each Quote Type
            </Typography>
            
            {aggregatedQuotes.map((quote) => (
              <Card key={quote.type} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {quote.type.toUpperCase()} Suppliers
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Select Suppliers</InputLabel>
                        <Select
                          multiple
                          value={selectedSuppliers.get(quote.type) || []}
                          onChange={(e) => updateSupplierSelection(quote.type, e.target.value as string[])}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const supplier = suppliers.find(s => s.id === value);
                                return (
                                  <Chip key={value} label={supplier?.name || value} size="small" />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {suppliers.map((supplier) => (
                            <MenuItem key={supplier.id} value={supplier.id}>
                              {supplier.name} ({supplier.email})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 2: // Configure Headers
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Quote Headers
            </Typography>
            
            {aggregatedQuotes.map((quote) => (
              <Card key={quote.type} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {quote.type.toUpperCase()} Header
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title"
                        value={quoteHeaders.get(quote.type)?.title || ''}
                        onChange={(e) => updateQuoteHeader(quote.type, 'title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={quoteHeaders.get(quote.type)?.description || ''}
                        onChange={(e) => updateQuoteHeader(quote.type, 'description', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={quoteHeaders.get(quote.type)?.priority || 3}
                          onChange={(e) => updateQuoteHeader(quote.type, 'priority', e.target.value)}
                        >
                          <MenuItem value={1}>1 - Low</MenuItem>
                          <MenuItem value={2}>2 - Normal</MenuItem>
                          <MenuItem value={3}>3 - Standard</MenuItem>
                          <MenuItem value={4}>4 - High</MenuItem>
                          <MenuItem value={5}>5 - Urgent</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Special Instructions"
                        value={quoteHeaders.get(quote.type)?.special_instructions || ''}
                        onChange={(e) => updateQuoteHeader(quote.type, 'special_instructions', e.target.value)}
                        placeholder="Special instructions for suppliers"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 3: // Upload Files
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Files for Positions
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload drawings, specifications, and documentation for each position. Files will be sent to suppliers with the quote requests.
            </Alert>
            
            {aggregatedQuotes.map((quote) => (
              <Card key={quote.type} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {quote.type.toUpperCase()} Files
                  </Typography>
                  
                  {quote.positions.map((pos, index) => (
                    <Box key={pos.customerPositionId} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Position {pos.customerPositionReference}: {pos.customerPosition.part_name}
                      </Typography>
                      
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          size="small"
                        >
                          Upload Files
                          <input
                            type="file"
                            hidden
                            multiple
                            accept=".pdf,.dwg,.step,.iges,.jpg,.png"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length > 0) {
                                handleFileUpload(pos.customerPositionId, quote.type, files);
                              }
                            }}
                          />
                        </Button>
                      </Box>
                      
                      {positionFiles[`${pos.customerPositionId}_${quote.type}`] && (
                        <Box>
                          {positionFiles[`${pos.customerPositionId}_${quote.type}`].files.map((file, fileIndex) => (
                            <Chip
                              key={fileIndex}
                              icon={<AttachFileIcon />}
                              label={file.name}
                              color="success"
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 4: // Create Quotes
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Ready to Create Supplier Quotes
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              All configurations complete. Ready to create {aggregatedQuotes.reduce((total, quote) => total + (selectedSuppliers.get(quote.type)?.length || 0), 0)} supplier quotes.
            </Alert>
            
            {aggregatedQuotes.map((quote) => {
              const supplierIds = selectedSuppliers.get(quote.type) || [];
              const selectedSupplierNames = supplierIds.map(id => suppliers.find(s => s.id === id)?.name).filter(Boolean);
              
              return (
                <Card key={quote.type} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      {quote.type.toUpperCase()} Quote
                    </Typography>
                    <Typography><strong>Positions:</strong> {quote.positions.length}</Typography>
                    <Typography><strong>Suppliers:</strong> {selectedSupplierNames.join(', ')}</Typography>
                    <Typography><strong>Files:</strong> {Object.keys(positionFiles).filter(key => key.includes(quote.type)).length} position(s) with files</Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        );

      default:
        return null;
    }
  };

  const canProceedToNextStep = () => {
    switch (step) {
      case 0: // Review
        return aggregatedQuotes.length > 0;
      case 1: // Suppliers
        return Array.from(selectedSuppliers.values()).some(suppliers => suppliers.length > 0);
      case 2: // Headers
        return Array.from(quoteHeaders.values()).every(header => header.title);
      case 3: // Files
        return true; // Files are optional
      case 4: // Create
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canProceedToNextStep()) {
      setStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QuoteIcon />
          Quote Aggregation Manager
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <LinearProgress 
            variant="determinate" 
            value={(step / (steps.length - 1)) * 100} 
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" align="center">
            Step {step + 1} of {steps.length}: {steps[step]}
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box sx={{ mt: 2 }}>
          {renderStepContent(step)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {step > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || !canProceedToNextStep()}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={generateSupplierQuotes}
            disabled={loading}
            startIcon={loading ? null : <CheckCircleIcon />}
          >
            {loading ? 'Creating Quotes...' : 'Create Supplier Quotes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}